// Task #3976 — Admin "Coming Soon" feature gating.
//
// This is the SINGLE shared source of truth for what can be gated. Both the
// client (nav visibility + route overlay + tab gating) and the server (generic
// blocking middleware + per-user resolver endpoint) import this registry and
// the pure helpers below, so a gateable target is defined exactly once.
//
// A "gate" is stored on the existing `beta_features` row for a target key
// (gateEnabled / gateMode / gateMessage). Per-user bypass reuses
// `user_beta_access` grants (admins always bypass). Nothing here touches the
// self-opt-in beta flow (isActive / allowSelfOptIn) — gating is orthogonal.

export type GateMode = "hidden" | "coming_soon";

export type GateableTarget = {
  /** Stable key — also the `beta_features.key` used to store the gate + grants. */
  key: string;
  /** Human label shown in the admin UI. */
  name: string;
  /** Optional admin-facing description of what gets gated. */
  description?: string;
  /**
   * Parent group key. Set on sub-tab targets (e.g. the Revenue tabs) so the
   * admin UI can nest them and so the generic client ROUTE resolver can skip
   * them (their routes are prefixes of each other — they are gated by the
   * owning page via their key instead). Top-level section targets omit this.
   */
  group?: string;
  /** Sidebar hrefs this target controls (exact membership — never prefix). */
  navHrefs: string[];
  /** Client route path prefixes this target covers (boundary-prefix match). */
  routePrefixes: string[];
  /** Exact client routes, for a tab that lives at its section's root. */
  routeExactPaths?: string[];
  /** Server API prefixes (boundary match; :parameter segments are supported). */
  apiPrefixes: string[];
};

// The registry. Adding a new gateable target is pure data — no engine change.
export const GATEABLE_TARGETS: GateableTarget[] = [
  {
    key: "pipeline",
    name: "Pipeline",
    description: "Buy Box, Underwriting and the Acquisitions pipeline.",
    navHrefs: [
      "/app/buy-box/settings",
      "/app/buy-box/agents",
      "/app/buy-box/reports",
      "/app/buy-box/saved",
      "/app/underwriting",
      "/app/acquisitions",
    ],
    routePrefixes: ["/app/buy-box", "/app/underwriting", "/app/acquisitions"],
    apiPrefixes: [
      "/api/app/buy-box",
      "/api/app/underwriting",
      "/api/app/acquisitions",
    ],
  },
  {
    // Legacy registry entry; hard-off for everyone and excluded from admin controls.
    key: "property_launch",
    name: "Launch",
    description: "Retired property-launch wizard. Switched off in code for everyone, including administrators.",
    navHrefs: ["/app/property-launch"],
    routePrefixes: ["/app/property-launch"],
    apiPrefixes: [],
  },
  {
    key: "members",
    name: "Members",
    description: "Members directory, vehicles and booking requests.",
    navHrefs: ["/app/members", "/app/members/vehicles", "/app/members/booking-requests"],
    routePrefixes: ["/app/members"],
    apiPrefixes: [
      "/api/app/members",
      "/api/app/member-financials",
      "/api/app/booking-requests",
      "/api/app/vehicles",
      "/api/app/properties/:id/member-vehicles",
    ],
  },
  {
    key: "operations",
    name: "Operations",
    description: "Service forms, vendor directory and card settings, approvals and marketplace.",
    navHrefs: [
      "/app/operations/service-forms",
      "/app/operations/vendors",
      "/app/operations/approvals",
      "/app/operations/marketplace",
      "/app/operations/vendors/settings",
    ],
    // Narrow, per-feature prefixes — NEVER the bare "/app/operations" /
    // "/api/app/operations" roots, which would over-block sibling features that
    // are NOT part of this gate (Tasks at /api/app/operations/tasks, To-Dos,
    // Trash, Task Rules, Payments).
    routePrefixes: [
      "/app/operations/service-forms",
      "/app/operations/vendors",
      "/app/operations/approvals",
      "/app/operations/marketplace",
    ],
    // Only APIs belonging to these routes. Owners, tasks and payments remain
    // independent; vendor settings uses separate card/assignment API bases.
    apiPrefixes: [
      "/api/app/operations/vendors",
      "/api/app/operations/approvals",
      "/api/app/operations/marketplace",
      "/api/app/service-forms",
      "/api/app/operations/vendor-credit-cards",
      "/api/app/operations/property-card-assignments",
    ],
  },
  {
    key: "devices",
    name: "Devices",
    description: "Smart locks and thermostats.",
    navHrefs: [
      "/app/devices",
      "/app/devices/smart-locks",
      "/app/devices/thermostats",
    ],
    routePrefixes: ["/app/devices"],
    apiPrefixes: ["/api/app/devices"],
  },
  {
    key: "revenue",
    name: "Revenue",
    description: "The entire Revenue section — Profit & Loss, Income, Expenses, Statements and more.",
    // NOTE: "/app/financials/collections" is intentionally NOT listed here — the
    // standalone `collections` target below owns that link's nav visibility +
    // badge so a hidden Revenue gate can't remove the independent Collections
    // link. (The Revenue routePrefix still covers the path, but gateBlockForRoute
    // lets the more-specific Collections gate/grant shadow it.)
    navHrefs: [
      "/app/financials",
      "/app/financials/income",
      "/app/financials/expenses",
      "/app/financials/recurring",
      "/app/financials/settings",
      "/app/financials/mail-inbox",
    ],
    routePrefixes: ["/app/financials"],
    apiPrefixes: [
      "/api/app/financials",
      "/api/app/income",
      "/api/app/expenses",
      "/api/app/expense-category-settings",
      "/api/app/income-category-settings",
      "/api/app/expense-ai-suggestion-events",
      "/api/app/expense-ai-suggestion-stats",
      "/api/app/mercury-statements",
      "/api/app/mercury/cards",
      "/api/app/mercury/credit-balance",
      "/api/app/mercury-connection",
      "/api/app/bank-feeds",
      "/api/app/mail-documents",
    ],
  },
  {
    // Task #3976 follow-on — Collections (member arrears + automated payment
    // reminders) ships behind a "Coming Soon" gate until its public launch.
    // A standalone top-level target (NOT a revenue sub-tab) so its own gate +
    // grants are fully independent of the Revenue section, in all three planes:
    //  • ROUTE: its prefix (/app/financials/collections) is strictly longer than
    //    revenue's (/app/financials); gateBlockForRoute lets the more-specific
    //    Collections gate/grant shadow the Revenue block on this path (so a
    //    Collections grant unlocks the page even if Revenue is also gated).
    //  • NAV: this target (not revenue) owns the collections nav href, so a
    //    hidden Revenue gate never removes the independent Collections link.
    //  • API: its root (/api/app/collections) is not under any revenue apiPrefix,
    //    so the server middleware gates it independently.
    key: "collections",
    name: "Collections",
    description: "Retired member arrears and payment reminders. Switched off in code for everyone, including administrators.",
    navHrefs: ["/app/financials/collections"],
    routePrefixes: ["/app/financials/collections"],
    apiPrefixes: ["/api/app/collections"],
  },
  // ── Revenue sub-tabs (group: "revenue") ──────────────────────────────────
  // Gated individually by the Financials page via their key (not the generic
  // route resolver — their routes are prefixes of each other). Each still
  // carries a distinct API prefix so the server middleware can block it.
  {
    key: "revenue_pnl",
    name: "Profit & Loss",
    description: "The Profit & Loss tab of the Revenue section.",
    group: "revenue",
    navHrefs: ["/app/financials"],
    // P&L lives at the section root, but its grant must not cover sibling tabs.
    routePrefixes: [],
    routeExactPaths: ["/app/financials"],
    apiPrefixes: ["/api/app/financials/pnl"],
  },
  {
    key: "revenue_income",
    name: "Income",
    description: "The Income tab of the Revenue section.",
    group: "revenue",
    navHrefs: ["/app/financials/income"],
    routePrefixes: ["/app/financials/income"],
    apiPrefixes: ["/api/app/income", "/api/app/income-category-settings"],
  },
  {
    key: "revenue_statements",
    name: "Statements",
    description: "The Mercury bank Statements tab of the Revenue section.",
    group: "revenue",
    navHrefs: [],
    routePrefixes: ["/app/financials/statements"],
    apiPrefixes: ["/api/app/mercury-statements"],
  },
  {
    key: "revenue_cards",
    name: "Cards",
    description: "The Mercury Cards tab of the Revenue section.",
    group: "revenue",
    navHrefs: ["/app/financials/cards"],
    routePrefixes: ["/app/financials/cards"],
    apiPrefixes: ["/api/app/mercury/cards", "/api/app/mercury/credit-balance"],
  },
];

export const GATEABLE_TARGET_KEYS: ReadonlySet<string> = new Set(
  GATEABLE_TARGETS.map((t) => t.key),
);

const TARGET_BY_KEY = new Map(GATEABLE_TARGETS.map((t) => [t.key, t] as const));

export function getGateableTarget(key: string): GateableTarget | undefined {
  return TARGET_BY_KEY.get(key);
}

export function isGateableTargetKey(key: string): boolean {
  return TARGET_BY_KEY.has(key);
}

/**
 * Boundary-aware prefix match. `/api/app/members` matches `/api/app/members`
 * and `/api/app/members/123` but NOT `/api/app/members-archive`.
 */
export function pathMatchesPrefix(path: string, prefix: string): boolean {
  if (path === prefix) return true;
  const withSlash = prefix.endsWith("/") ? prefix : prefix + "/";
  return path.startsWith(withSlash);
}

/** Match a parameterized API prefix without widening its static boundaries. */
export function apiPathMatchesPrefix(path: string, prefix: string): boolean {
  if (!prefix.includes("/:")) return pathMatchesPrefix(path, prefix);
  const segments = path.split("/");
  const expected = prefix.split("/");
  return segments.length >= expected.length && expected.every((segment, index) =>
    segment.startsWith(":") ? !!segments[index] : segment === segments[index],
  );
}
export type TargetMatch = { target: GateableTarget; prefix: string };

/** All targets whose API prefixes cover `path` (each match carries the prefix). */
export function matchApiTargets(path: string): TargetMatch[] {
  const out: TargetMatch[] = [];
  for (const target of GATEABLE_TARGETS) {
    for (const prefix of target.apiPrefixes) {
      if (apiPathMatchesPrefix(path, prefix)) out.push({ target, prefix });
    }
  }
  return out;
}

/**
 * Top-level (section) targets whose ROUTE prefixes cover `loc`. Sub-tab targets
 * (those with a `group`) are intentionally excluded — their routes nest under
 * the section route, so the owning page gates them by key instead.
 */
export function matchSectionRouteTargets(loc: string): TargetMatch[] {
  const out: TargetMatch[] = [];
  for (const target of GATEABLE_TARGETS) {
    if (target.group) continue;
    for (const prefix of target.routePrefixes) {
      if (pathMatchesPrefix(loc, prefix)) out.push({ target, prefix });
    }
  }
  return out;
}

/** Targets that control a given sidebar href (exact membership). */
export function targetsForNavHref(href: string): GateableTarget[] {
  return GATEABLE_TARGETS.filter((t) => t.navHrefs.includes(href));
}

export type GateState = { mode: GateMode; message: string | null };
export type GateDecision = { blocked: boolean; mode?: GateMode; message?: string | null; key?: string };

/**
 * Specificity ordering for two overlapping matches. The longer matched prefix
 * wins; on an exact-length tie a grouped sub-tab target (e.g. revenue_statements
 * and revenue_cards both share the parent's API prefix) beats its parent section
 * (revenue) so the more granular gate's message + key is the one surfaced.
 */
function isMoreSpecific(a: TargetMatch, b: TargetMatch): boolean {
  if (a.prefix.length !== b.prefix.length) return a.prefix.length > b.prefix.length;
  return !!a.target.group && !b.target.group;
}

/**
 * Core resolution shared by the server middleware and any client check. Among
 * the matched targets that are gated (getGate returns a state) and for which
 * the user is NOT exempt, pick the most specific (see `isMoreSpecific`) and
 * return its mode + message. Returns `{ blocked: false }` when nothing applies.
 *
 * Sub-tab grant bypasses the ancestor section gate, path-scoped (Task #3984):
 * if an admin gates a whole section (e.g. `revenue`) but grants a user access to
 * a single sub-tab (e.g. `revenue_cards`), that grant lets the user into the
 * sub-tab's paths even though the parent section is still gated. Because
 * `matches` is already filtered to the targets that cover the current path, any
 * matched sub-tab whose `group` equals a section key here also covers this path,
 * so an exemption on that sub-tab is treated as an exemption from the parent
 * section gate FOR THIS PATH ONLY — the rest of the section stays gated (a Cards
 * grant never unlocks Income/P&L, whose paths the Cards sub-tab doesn't match).
 */
export function resolveGateDecision(
  matches: TargetMatch[],
  getGate: (key: string) => GateState | undefined,
  isExempt: (key: string) => boolean,
): GateDecision {
  // Section keys the user is exempt from on THIS path by virtue of an exempting
  // grant on one of the section's sub-tabs that also covers this path.
  const sectionExemptViaSubTab = new Set<string>();
  for (const m of matches) {
    if (m.target.group && isExempt(m.target.key)) {
      sectionExemptViaSubTab.add(m.target.group);
    }
  }

  let best: { match: TargetMatch; gate: GateState } | null = null;
  for (const m of matches) {
    const gate = getGate(m.target.key);
    if (!gate) continue;
    if (isExempt(m.target.key)) continue;
    if (sectionExemptViaSubTab.has(m.target.key)) continue;
    if (!best || isMoreSpecific(m, best.match)) {
      best = { match: m, gate };
    }
  }
  if (!best) return { blocked: false };
  return { blocked: true, mode: best.gate.mode, message: best.gate.message, key: best.match.target.key };
}

// Per-user, per-target resolution returned to the client by GET
// /api/app/feature-gates. Targets with an ENABLED gate are returned, PLUS any
// granted sub-tab whose parent section is gated (so the client can apply the
// path-scoped sub-tab→section exemption, Task #4017). A target absent from the
// response is ungated for that user.
export type ResolvedGate = {
  key: string;
  name: string;
  group?: string;
  mode: GateMode;
  message: string | null;
  /** true when the gate applies to this user (enabled AND not exempt). */
  gated: boolean;
  /** true when the user bypasses the gate (admin or granted). */
  exempt: boolean;
};

export function resolveEnabledGatesForUser(
  getGate: (key: string) => GateState | undefined,
  isExempt: (key: string) => boolean,
): ResolvedGate[] {
  const out: ResolvedGate[] = [];
  for (const t of GATEABLE_TARGETS) {
    const gate = getGate(t.key);
    if (gate) {
      const exempt = isExempt(t.key);
      out.push({
        key: t.key,
        name: t.name,
        group: t.group,
        mode: gate.mode,
        message: gate.message,
        gated: !exempt,
        exempt,
      });
      continue;
    }
    // Not gated itself. Still surface a granted SUB-TAB whose parent SECTION is
    // gated, so the client can apply the path-scoped sub-tab→section exemption
    // (Task #4017) — mirroring resolveGateDecision's sectionExemptViaSubTab.
    // The entry is never `gated`; it only carries the grant (exempt: true) and
    // its `group`, which the client route/nav helpers key off of.
    if (t.group && isExempt(t.key)) {
      const parent = getGate(t.group);
      if (parent) {
        out.push({
          key: t.key,
          name: t.name,
          group: t.group,
          mode: parent.mode,
          message: null,
          gated: false,
          exempt: true,
        });
      }
    }
  }
  return out;
}

export function targetMatchesRoute(target: GateableTarget, path: string): boolean {
  return !!target.routeExactPaths?.includes(path) ||
    target.routePrefixes.some((prefix) => pathMatchesPrefix(path, prefix));
}
